import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, HelpCircle, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ConfirmContext = createContext(null);

const VARIANT_STYLES = {
  default: {
    Icon: HelpCircle,
    iconClass: 'text-blue-500 dark:text-blue-400',
    iconBg: 'bg-blue-50 dark:bg-blue-900/30',
    confirmClass: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  warning: {
    Icon: AlertTriangle,
    iconClass: 'text-amber-500 dark:text-amber-400',
    iconBg: 'bg-amber-50 dark:bg-amber-900/30',
    confirmClass: 'bg-amber-600 hover:bg-amber-700 text-white',
  },
  danger: {
    Icon: Trash2,
    iconClass: 'text-red-500 dark:text-red-400',
    iconBg: 'bg-red-50 dark:bg-red-900/30',
    confirmClass: 'bg-red-600 hover:bg-red-700 text-white',
  },
};

export function ConfirmProvider({ children }) {
  const { t } = useTranslation();
  const [state, setState] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: '',
    cancelLabel: '',
    variant: 'default',
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        title: opts.title || t('confirmDialog.title'),
        message: opts.message || '',
        confirmLabel: opts.confirmLabel || t('confirmDialog.confirm'),
        cancelLabel: opts.cancelLabel || t('confirmDialog.cancel'),
        variant: opts.variant || 'default',
      });
    });
  }, [t]);

  const handleClose = (result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  const variant = VARIANT_STYLES[state.variant] || VARIANT_STYLES.default;
  const { Icon } = variant;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={state.open} onOpenChange={(o) => { if (!o) handleClose(false); }}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-800">
          <DialogHeader>
            <div className="flex items-start gap-4">
              <div className={`${variant.iconBg} p-3 rounded-full flex-shrink-0`}>
                <Icon className={`w-6 h-6 ${variant.iconClass}`} />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-lg font-semibold text-slate-900 dark:text-white">
                  {state.title}
                </DialogTitle>
                {state.message && (
                  <DialogDescription className="mt-2 text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line">
                    {state.message}
                  </DialogDescription>
                )}
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="mt-2 flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={() => handleClose(false)}
              className="w-full sm:w-auto"
            >
              {state.cancelLabel}
            </Button>
            <Button
              onClick={() => handleClose(true)}
              className={`w-full sm:w-auto ${variant.confirmClass}`}
              autoFocus
            >
              {state.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return ctx;
}
